import React, { useState, useCallback } from 'react';
import { upload } from '../../../api/client';
import { handleApiError, showErrorNotification } from '../../../utils/errorHandler';

interface FileUploaderProps {
  onUploadSuccess: (url: string) => void;
  onUploadError: (error: string) => void;
  acceptedTypes?: string[];
  maxSize?: number; // in bytes
  label?: string;
  disabled?: boolean;
  /** 上传接口路径，默认 /upload */
  uploadUrl?: string;
}

const FileUploader = ({
  onUploadSuccess,
  onUploadError,
  acceptedTypes = ['image/jpeg', 'image/png', 'image/gif'],
  maxSize = 5 * 1024 * 1024, // 5MB
  label = '点击上传文件',
  disabled = false,
  uploadUrl = '/upload',
}: FileUploaderProps) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 文件类型验证
    if (!acceptedTypes.includes(file.type)) {
      onUploadError(`不支持的文件类型。请上传 ${acceptedTypes.map(type => type.split('/')[1]).join(', ')} 文件。`);
      return;
    }

    // 文件大小验证
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / 1024 / 1024).toFixed(1);
      onUploadError(`文件大小超过限制。最大允许 ${maxSizeMB}MB。`);
      return;
    }

    setLoading(true);
    setProgress(0);

    let progressInterval: ReturnType<typeof setTimeout>;

    try {
      // 模拟上传进度
      progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const uploadResult = await upload<{ url: string }>(uploadUrl, file);
      
      clearInterval(progressInterval);
      setProgress(100);
      
      if (uploadResult && uploadResult.url) {
        onUploadSuccess(uploadResult.url);
        // 重置进度
        setTimeout(() => setProgress(0), 500);
      } else {
        throw new Error('上传失败，未返回文件URL');
      }
    } catch (error) {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      const errorInfo = handleApiError(error);
      onUploadError(errorInfo.message);
      showErrorNotification(errorInfo);
    } finally {
      setLoading(false);
    }
  }, [acceptedTypes, maxSize, onUploadError, onUploadSuccess, uploadUrl]);

  return (
    <div className="w-full">
      <input
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={handleFileChange}
        disabled={disabled || loading}
        className="hidden"
        id="file-upload-input"
      />
      <label
        htmlFor="file-upload-input"
        className={`w-full flex items-center justify-center p-4 border-2 border-dashed rounded-lg cursor-pointer transition-all ${disabled || loading ? 'opacity-60 cursor-not-allowed' : 'border-gray-200 hover:border-teal-400 hover:bg-teal-50'}`}
      >
        {loading ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-teal-500 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-sm text-gray-600">上传中... {progress}%</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full text-gray-400">
              <i className="ri-upload-cloud-line text-lg" />
            </div>
            <span className="text-sm text-gray-500">{label}</span>
            <span className="text-xs text-gray-400">
              支持 {acceptedTypes.map(type => type.split('/')[1]).join(', ')}，最大 {((maxSize / 1024 / 1024).toFixed(1))}MB
            </span>
          </div>
        )}
      </label>
    </div>
  );
};

export default FileUploader;
